import _ from 'lodash'
import dayjs from 'dayjs'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'

import Alert from '@mui/material/Alert'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

import { AvailabilityMeetingPlace } from '@/core/models/meeting-link.model'
import type { AvailabilityWizardValues } from '../CreateAvailabilityWizard'

export default function ReviewStep() {
  const { i18n, t } = useTranslation()
  const { values } = useFormikContext<AvailabilityWizardValues>()

  const meetingPlaceLabel = {
    [AvailabilityMeetingPlace.CENTER]: t('page.meetingLink.availabilitiesTable.cell.meetingPlace.center'),
    [AvailabilityMeetingPlace.AT_HOME]: t('page.meetingLink.availabilitiesTable.cell.meetingPlace.atHome'),
    [AvailabilityMeetingPlace.VISIO_CONFERENCE]: t('page.meetingLink.availabilitiesTable.cell.meetingPlace.visioConference'),
    [AvailabilityMeetingPlace.SECOND_OPINION]: t('page.meetingLink.availabilitiesTable.cell.meetingPlace.secondOpinion'),
  }

  const activeDays = values.recurrenceDays
    ?.map((rd, index) => (rd.periods.length > 0 ? index : null))
    .filter((d) => d !== null)
    .map((d) => _.capitalize(dayjs().locale(i18n.language).day(d!).format('ddd')))

  const scheduleDisplay = activeDays && activeDays.length > 0
    ? activeDays.join(', ')
    : t('page.meetingLink.wizard.review.noSchedule', 'No days selected')

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography variant="h6">
          {t('page.meetingLink.wizard.review.title', 'Review your availability')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('page.meetingLink.wizard.review.subtitle', 'Here\'s what patients will be able to book')}
        </Typography>
      </Stack>

      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
        <CardContent>
          <Stack spacing={1}>
            <SummaryRow
              label={t('page.meetingLink.wizard.review.type', 'Type')}
              value={meetingPlaceLabel[values.meetingPlace]}
            />
            <SummaryRow
              label={t('field.speciality.label', { context: 'plural' })}
              value={values.specialities?.join(', ') || '—'}
            />
            <SummaryRow
              label={t('page.meetingLink.wizard.review.duration', 'Duration')}
              value={`${values.avgMeetingDuration} min`}
            />
            {values.meetingPlace === AvailabilityMeetingPlace.CENTER && values.address && (
              <SummaryRow
                label={t('field.address.label')}
                value={values.address.description}
              />
            )}
            {values.meetingPlace === AvailabilityMeetingPlace.AT_HOME && (
              <SummaryRow
                label={t('field.serviceArea.label')}
                value={
                  values.serviceArea?.allowedPostalCodes
                    ? String(values.serviceArea.allowedPostalCodes)
                    : values.serviceArea?.center?.description
                      ? `${values.serviceArea.center.description} (${values.serviceArea.radiusKm || '?'} km)`
                      : '—'
                }
              />
            )}
            <SummaryRow
              label={t('page.meetingLink.wizard.review.schedule', 'Schedule')}
              value={scheduleDisplay}
            />
            <SummaryRow
              label={t('page.meetingLink.wizard.review.payment', 'Payment')}
              value={t('page.meetingLink.wizard.review.paymentNotConfigured', 'Not configured')}
            />
          </Stack>
        </CardContent>
      </Card>

      <Alert
        severity="success"
        icon={<CheckCircleOutlineIcon />}
        sx={{ bgcolor: 'success.50' }}
      >
        <Typography variant="body2" fontWeight={500}>
          {t('page.meetingLink.wizard.review.confirmation', 'Patients will see available slots starting tomorrow')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('page.meetingLink.wizard.review.confirmationSub', 'They\'ll book through your meeting link page')}
        </Typography>
      </Alert>

      <Alert severity="info" variant="outlined">
        {t('page.meetingLink.wizard.review.paymentHint', 'Want to require payment? You can configure this in the availability settings after creation.')}
      </Alert>
    </Stack>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value}</Typography>
    </Stack>
  )
}
